import type { SpinTransportErrorKind } from "@slot/client-engine";
import type { SystemModalConfig } from "./SystemModalTypes";

/** What a failure says and how it ends: a reload (the only offer for anything unverified) or an OK. */
export interface FailureCopy {
  titleKey: string;
  titleFallback: string;
  messageKey: string;
  messageFallback: string;
  reload: boolean;
}

/** A transport kind, or a thrown error that is none of them. */
export type SpinFailureKind = SpinTransportErrorKind | "unexpected";

/**
 * A spin the server refused at 5xx with a rejection body (decision 79): no round was placed and the charge did not
 * complete, so the bet will be returned. "Will be": the refund may still be queued with the server's worker (ADR 0068
 * § 14), and the balance on the reply may not show it yet.
 */
const SPIN_NOT_PLACED: FailureCopy = {
  titleKey: "spin_not_placed.popup.title",
  titleFallback: "Spin Not Placed",
  messageKey: "spin_not_placed.popup.message",
  messageFallback: "The spin did not go through, and your bet will be returned to your balance. Please try again in a moment.",
  reload: false,
};

/** The rejection codes the shell renders in its own words; every other code is an error (user, 2026-09-13). */
const REJECTION_COPY: Readonly<Record<string, FailureCopy>> = {
  insufficient_funds: {
    titleKey: "lowbalance.popup.title",
    titleFallback: "Insufficient Funds",
    messageKey: "lowbalance.popup.message",
    messageFallback: "Please reduce the bet size or add funds to your account",
    reload: false,
  },
  // Another spin for this player is still executing — in practice the game open in a second window
  // or on a second device. The backend refuses it at its per-player lock, before any debit, with a
  // 409 and no balance; the pre-request balance goes back on the readout and an OK leaves the
  // player in the game (user, 2026-09-24).
  player_spin_in_flight: {
    titleKey: "spin_in_flight.popup.title",
    titleFallback: "Spin in Progress",
    messageKey: "spin_in_flight.popup.message",
    messageFallback: "Another spin on your account is still running, possibly in another window. This spin was not placed, and no bet was taken. Wait a moment, then spin again.",
    reload: false,
  },
  // The wallet refused the charge on a limit set on the account (403, with a balance): nothing was bet, the player stays
  // in the game and may change the bet or stop (decision 79).
  limit_reached: {
    titleKey: "limit_reached.popup.title",
    titleFallback: "Limit Reached",
    messageKey: "limit_reached.popup.message",
    messageFallback: "You have reached a limit set on your account. This spin was not placed, and no bet was taken.",
    reload: false,
  },
  // The previous round is still being settled — its unpaid win or its refund still going out — when the next spin asks
  // for that first (409, nothing charged): a later spin goes through once it is (decision 79).
  previous_round_unsettled: {
    titleKey: "previous_round_unsettled.popup.title",
    titleFallback: "Previous Round Being Settled",
    messageKey: "previous_round_unsettled.popup.message",
    messageFallback: "Your previous round is still being settled. This spin was not placed, and no bet was taken. Please try again in a moment.",
    reload: false,
  },
  // The server answered and placed no round: its wallet timed out, was unavailable, or limited the rate (503 with a
  // rejection body and a balance). A reply that never came, or a 5xx without that body, stays a transport failure:
  // its outcome is unknown (decision 79).
  timeout: SPIN_NOT_PLACED,
  unavailable: SPIN_NOT_PLACED,
  rate_limited: SPIN_NOT_PLACED,
};

/**
 * A 429 from the backend's rate limit (decision 81): it refused the spin before anything was done, unlike the wallet's
 * `rate_limited` at 503 (a charge that did not complete), so nothing is returned because nothing was taken.
 */
const RATE_LIMITED: FailureCopy = {
  titleKey: "rate_limited.popup.title",
  titleFallback: "Please Wait",
  messageKey: "rate_limited.popup.message",
  messageFallback: "Too many requests came in a short time. This spin was not placed, and no bet was taken. Wait a moment, then spin again.",
  reload: false,
};

/**
 * A launch the backend refused for good (decision 81): the link expired or is not valid. A reload would open the same
 * link, so the boot's message has no button.
 */
const LAUNCH_REFUSED: FailureCopy = {
  titleKey: "launch_refused.title",
  titleFallback: "Game Link Not Valid",
  messageKey: "launch_refused.message",
  messageFallback: "This game link has expired or isn't valid. Return to the casino and open the game again.",
  reload: false,
};

const GENERIC: FailureCopy = {
  titleKey: "system.error.generic.title",
  titleFallback: "Something went wrong",
  messageKey: "system.error.generic.message",
  messageFallback: "Please reload the game. Any round in progress is saved on the server. If this keeps happening, contact support.",
  reload: true,
};

const TRANSPORT_COPY: Readonly<Record<SpinFailureKind, FailureCopy>> = {
  network: { titleKey: "connectivity.network.title", titleFallback: "Connection Lost", messageKey: "connectivity.network.message", messageFallback: "The game cannot reach the server. Check your internet connection, then reload the game. Any round in progress is saved on the server.", reload: true },
  server: { titleKey: "connectivity.server.title", titleFallback: "Server Unavailable", messageKey: "connectivity.server.message", messageFallback: "The game server is temporarily unavailable. Wait a moment, then reload the game. Any round in progress is saved on the server.", reload: true },
  protocol: { titleKey: "connectivity.protocol.title", titleFallback: "Unexpected Server Response", messageKey: "connectivity.protocol.message", messageFallback: "The game received a response it could not read. Please reload the game. Any round in progress is saved on the server. If this keeps happening, contact support.", reload: true },
  session: { titleKey: "connectivity.session.title", titleFallback: "Session Ended", messageKey: "connectivity.session.message", messageFallback: "Your session has ended. Reload the game or open it again from the casino lobby. Any round in progress is saved on the server.", reload: true },
  unexpected: GENERIC,
};

/**
 * A 401 `session_superseded` (decision 80): the game was opened again, in another window or on another device, and
 * that boot's play session ended this one. Nothing was done with the refused request; a reload plays here again.
 */
const SESSION_SUPERSEDED: FailureCopy = {
  titleKey: "connectivity.session.title",
  titleFallback: "Session Ended",
  messageKey: "connectivity.session_superseded.message",
  messageFallback: "This game was opened in another window or on another device, so this session has ended. Reload to play here. Any round in progress is saved on the server.",
  reload: true,
};

const FLOW_COPY = {
  replay: { titleKey: "replay.failed.title", titleFallback: "Replay Failed", messageKey: "replay.failed.message", messageFallback: "This round could not be replayed. Reload to try again.", reload: true },
  restore: { titleKey: "restore.error.title", titleFallback: "Restore Failed", messageKey: "restore.error.message", messageFallback: "Your previous game could not be completed. It is still saved on the server. Reload to try again, and contact support if this keeps happening.", reload: true },
} as const satisfies Record<string, FailureCopy>;

/** A server verdict on a spin: a known code in the shell's words, anything else as an error; a 429 by its status. */
export function rejectionFailure(statusMessage: string | undefined, statusCode?: number): FailureCopy {
  if (statusCode === 429) return RATE_LIMITED;
  return (statusMessage && REJECTION_COPY[statusMessage]) || GENERIC;
}

export function launchRefusedFailure(): FailureCopy {
  return LAUNCH_REFUSED;
}

/** A transport kind in the shell's words; a session the game's opening elsewhere ended says so (decision 80). */
export function transportFailure(kind: SpinFailureKind, statusMessage?: string): FailureCopy {
  return kind === "session" && statusMessage === "session_superseded" ? SESSION_SUPERSEDED : TRANSPORT_COPY[kind];
}

export function flowFailure(flow: keyof typeof FLOW_COPY): FailureCopy {
  return FLOW_COPY[flow];
}

export function genericFailure(): FailureCopy {
  return GENERIC;
}

/** The modal for a failure: a reload is the only button and cannot be dismissed; an OK dismisses. */
export function failureModal(copy: FailureCopy, t: (key: string, fallback: string) => string): SystemModalConfig {
  return {
    title: t(copy.titleKey, copy.titleFallback),
    message: t(copy.messageKey, copy.messageFallback),
    primaryButtonText: copy.reload ? t("common.reload", "Reload") : t("common.ok", "OK"),
    dismissible: !copy.reload,
  };
}
